import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FaInfoCircle } from 'react-icons/fa';
import { BiLeftArrowAlt } from 'react-icons/bi';
import {
  MdRemoveRedEye,
  MdConfirmationNumber,
  MdPayments
} from 'react-icons/md';

import AnalyticsSkeleton from './AnalyticsSkeleton';

import { ErrorResponse } from 'legacy/common/utils/error';
import { currencyFormat } from 'legacy/common/utils/currency';

type OverallNumbersProps = {
  views: number;
  sales: number;
  revenue: number;
  isLoading: boolean;
  error: ErrorResponse;
  onBack?: () => void;
};

const OverallNumbers: React.FC<OverallNumbersProps> = ({
  views,
  sales,
  revenue,
  isLoading,
  error,
  onBack
}) => {
  const { t } = useTranslation(['events']);
  if (isLoading) {
    return (
      <div className="relative w-full bg-primary-200">
        <div className="w-full md:w-[630px] mx-auto">
          <AnalyticsSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-primary-200">
      {onBack && (
        <div
          className="pl-2 pt-2 md:absolute md:p-0 left-2 top-2 md:left-40 md:top-10 cursor-pointer"
          onClick={() => onBack()}
        >
          <BiLeftArrowAlt className="text-primary-500 text-4xl" />
        </div>
      )}

      <div
        className={classNames('w-full md:w-[630px] mx-auto', {
          'blur-sm': !!error?.message
        })}
      >
        <div className="rounded-2xl bg-primary-200 p-5">
          <div className="flex justify-between">
            <div className="text-2xl font-semibold flex items-center">
              {t('events:settings.trackingLinks.page.overallNumbers')}
            </div>
          </div>

          <div className="mt-6 bg-white p-5 rounded-2xl">
            <div className="text-sm text-gray-500 flex items-center font-normal">
              <MdRemoveRedEye className="mr-1 text-xl font-normal" />
              {t('events:settings.trackingLinks.page.totalViews')}
            </div>
            <div className="mt-2 text-3xl font-semibold">{views || 0}</div>
          </div>

          <div className="mt-6 flex">
            <div className="w-full bg-white p-5 rounded-2xl mr-2">
              <div className="text-sm text-gray-500 flex items-center font-normal">
                <MdConfirmationNumber size={14} className="mr-1" />
                {t('events:settings.trackingLinks.page.totalSales')}
              </div>
              <div className="mt-2 text-3xl font-semibold">{sales}</div>
            </div>

            <div className="w-full bg-white p-5 rounded-2xl ml-2">
              <div className="text-sm text-gray-500 flex items-center font-normal">
                <MdPayments size={14} className="mr-1" />
                {t('events:settings.trackingLinks.page.totalRevenue')}
              </div>
              <div className="mt-2 text-3xl font-semibold">
                {currencyFormat(revenue)}
              </div>
            </div>
          </div>
          <p className="text-xs my-5 text-gray-400 font-normal">
            {t('events:settings.trackingLinks.page.dataNote')}
          </p>
        </div>
      </div>

      {error?.message && (
        <div className="absolute left-0 right-0 top-0 bottom-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="p-4 bg-gray-100 rounded-2xl text-center shadow-md border-1 border-gray-400">
            <p className="text-xl flex justify-center items-center">
              <FaInfoCircle className="mr-2" size="12" />
              {error.message}
            </p>
            <p className="mt-4 text-lg font-semibold">
              {t('events:settings.trackingLinks.page.errorTryAgain')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverallNumbers;
